import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Loader2, Mail, Lock, User, AlertCircle, ArrowLeft, CheckCircle, Clock, Sparkles, Film, Clapperboard } from 'lucide-react';
import axios from 'axios';

const API = window.__API_BASE + '/api';

const FEATURES = [
  { icon: Clapperboard, text: 'AI Storyboarding' },
  { icon: Film, text: 'Automatic Keyframes' },
  { icon: Sparkles, text: 'Video Generation' },
];

export default function LoginPage() {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPendingApproval, setShowPendingApproval] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setShowPendingApproval(false);
    setLoading(true);

    try {
      if (isLogin) {
        const res = await login(email, password);
        if (!res.success) {
          if (res.error === 'pending_approval') {
            setShowPendingApproval(true);
          } else {
            setError(res.error || 'Invalid email or password');
          }
        }
      } else {
        const res = await register(email, password, name);
        if (res.pending_approval) {
          setSuccess(res.message);
          setShowPendingApproval(true);
        } else if (!res.success) {
          setError(res.error || 'Registration failed');
        }
      }
    } catch (e) {
      if (e.response?.data?.detail === 'pending_approval') {
        setShowPendingApproval(true);
      } else if (e.code === 'ERR_NETWORK') {
        setError('Network error - please check your connection');
      } else if (e.response?.status === 401) {
        setError('Invalid email or password');
      } else if (e.response?.status === 400) {
        setError(e.response?.data?.detail || 'Invalid request');
      } else {
        setError(e.response?.data?.detail || 'Something went wrong');
      }
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess('If an account exists, you will receive a password reset link.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong');
    }
    setLoading(false);
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-dark-app flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <button
            onClick={() => setShowForgotPassword(false)}
            className="flex items-center gap-2 text-txt-muted hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to login</span>
          </button>

          <div className="surface-card p-8">
            <div className="mb-8">
              <h1 className="font-heading text-2xl font-bold text-white mb-2">Reset Password</h1>
              <p className="text-txt-muted">Enter your email to receive a reset link</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-status-error/10 border border-status-error/20 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-status-error flex-shrink-0" />
                <p className="text-sm text-status-error">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-status-success/10 border border-status-success/20 rounded-lg flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-status-success flex-shrink-0" />
                <p className="text-sm text-status-success">{success}</p>
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-txt-muted" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@eipimedia.com"
                    className="input-dark pl-12"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Pending Approval View
  if (showPendingApproval) {
    return (
      <div className="min-h-screen bg-dark-app flex items-center justify-center p-6">
        <div className="w-full max-w-md surface-card p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-status-warning/20 flex items-center justify-center">
            <Clock className="w-8 h-8 text-status-warning" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-white mb-3">Pending Approval</h2>
          <p className="text-txt-muted mb-6">
            Your account is awaiting admin approval. You'll receive an email once approved.
          </p>
          <button
            onClick={() => {
              setShowPendingApproval(false);
              setIsLogin(true);
            }}
            className="btn-secondary"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Main Login/Register View
  return (
    <div className="min-h-screen flex flex-col lg:flex-row relative overflow-hidden bg-[#0a0612]">
      {/* Left/Top Side - Hero with Einstein+Picasso background */}
      <div className="relative overflow-hidden h-[35vh] lg:h-auto lg:w-1/2 lg:flex" style={{ zIndex: 2 }}>
        {/* Einstein+Picasso Background Image */}
        <div className="absolute inset-0 bg-[#2a1545]">
          <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at center, #3d2266 0%, #2a1545 50%, #1a0a2e 100%)' }}></div>
          <img 
            src="https://customer-assets.emergentagent.com/job_81b2f10e-de8b-40ad-8caa-ede44a4a6a64/artifacts/3tfek74k_hf_20260310_181830_39e39843-1925-46a4-8eb0-69de3deb1d5a.png" 
            alt="" 
            className="w-full h-full object-cover"
            style={{ opacity: 0.7, mixBlendMode: 'multiply' }}
          />
          {/* Dark gradient overlay for text readability */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,6,18,0.85) 0%, rgba(26,10,46,0.7) 50%, rgba(10,6,18,0.6) 100%)' }} />
          {/* Purple glow at top left */}
          <div className="absolute top-0 left-0 w-96 h-96 bg-purple-600/20 rounded-full blur-[100px]" />
          {/* Bottom fade to blend into form area on mobile */}
          <div className="absolute bottom-0 left-0 right-0 h-20 lg:hidden" style={{ background: 'linear-gradient(to top, #0a0612, transparent)' }} />
        </div>
        
        {/* Content */}
        <div className="relative flex flex-col p-6 lg:p-12 w-full" style={{ zIndex: 3 }}>
          {/* Logo + subtitle */}
          <div className="mb-auto">
            <img 
              src="/assets/eipi-logo.png" 
              alt="EiPi Media" 
              className="h-5 lg:h-10 w-auto object-contain brightness-0 invert"
              data-testid="login-logo"
            />
            <p className="text-purple-400/70 text-[8px] lg:text-xs mt-0.5 lg:mt-1 tracking-wider uppercase">Powered by AI</p>
          </div>

          {/* Main content — hidden on mobile, shown on desktop */}
          <div className="max-w-lg my-auto hidden lg:block">
            <h1 className="font-heading text-4xl font-bold text-white mb-6 leading-tight">
              From Script to Screen
            </h1>
            <p className="text-white/60 text-base mb-10">
              AI-generated storyboards, keyframes, and professional video output.
            </p>

            {/* Feature list */}
            <div className="space-y-4">
              {FEATURES.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <feature.icon className="w-5 h-5 text-purple-400" />
                  <span className="text-white/80 font-medium">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer - desktop only */}
          <p className="mt-auto text-white/40 text-sm hidden lg:block">
            © 2026 EiPi Media. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right/Bottom Side - Form */}
      <div className="flex-1 flex p-6 lg:p-12 relative bg-[#0a0612]" style={{ zIndex: 2 }}>
        <div className="w-full max-w-md mx-auto lg:mt-[28vh] mt-2">
          {/* Header */}
          <div className="mb-8">
            <h2 className="font-heading text-3xl font-bold text-white mb-2">
              {isLogin ? 'From Script to Screen' : 'Create account'}
            </h2>
            <p className="text-txt-muted">
              {isLogin ? 'Sign in to continue to Eipicenter' : 'Start creating AI-powered films'}
            </p>
          </div>

          {/* Error/Success messages */}
          {error && (
            <div className="mb-6 p-4 bg-status-error/10 border border-status-error/20 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-status-error flex-shrink-0" />
              <p className="text-sm text-status-error">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-status-success/10 border border-status-success/20 rounded-lg flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-status-success flex-shrink-0" />
              <p className="text-sm text-status-success">{success}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-2">Full Name</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-txt-muted" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="input-dark pl-12"
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-txt-secondary mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-txt-muted" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@eipimedia.com"
                  className="input-dark pl-12"
                  required
                  data-testid="email-input"
                />
              </div>
              <p className="mt-1.5 text-xs text-txt-muted">Only @eipimedia.com emails allowed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-txt-secondary mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-txt-muted" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-dark pl-12"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2 py-4"
              data-testid="submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          {/* Footer links */}
          <div className="mt-6 space-y-4">
            {isLogin && (
              <button
                onClick={() => setShowForgotPassword(true)}
                className="w-full text-center text-sm text-txt-muted hover:text-purple-400 transition-colors"
              >
                Forgot your password?
              </button>
            )}

            <div className="text-center text-sm text-txt-muted">
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                  setSuccess('');
                }}
                className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
